import React, { useCallback, useMemo, useState } from 'react';
import { ListDotIcon } from '../../assets/icons/ListDotIcon';
import { MenuItemProps } from '../../components/menu/components/menuItem/MenuItem';
import { Item, ItemBase, MenuPermissions } from '../../models/menus';
import { stringToReactElement } from '../../utils/ParsingUtils';

enum References {
  FORCE_CLOSE = -1,
  OPENED_ACTIVE = 0,
}

interface Props {
  roles?: string[];
  idFerramenta: string;
  menus: Item[];
  menusLoading?: boolean;
  history: { push: (url: string) => void; location: unknown };
  getProgramaLabel: (str: string) => string;
  openModalProfileWarning: (
    label: string,
    plural?: boolean,
    isPerfil?: boolean,
    labelPrograma?: boolean
  ) => void;
  allProfilesForProgramas: { [k: string]: string[] };
  isRoute: (str: string, reg: RegExp | string) => boolean;
  closeMenu: () => void;
  incongruencia?: boolean;
  openModalIncongruencia?: () => void;
}

const sortItems = <T extends ItemBase>(a: T, b: T) => a.ordem - b.ordem;

export default function usePublicMenuList(props: Props) {
  const hasPermission = useCallback(
    (need: MenuPermissions) => {
      const needPermission =
        (need.roles && need.roles.length) ||
        (need.programas && need.programas.length);

      const hasRole =
        need.roles &&
        need.roles.length &&
        need.roles.some((role) => props.roles && props.roles.includes(role));

      const hasRoleByPrograma =
        need.programas &&
        need.programas.length &&
        need.programas.some(
          (prog) =>
            props.allProfilesForProgramas[prog] &&
            props.allProfilesForProgramas[prog].some(
              (profile: string) => props.roles && props.roles.includes(profile)
            )
        );

      return !needPermission || hasRole || hasRoleByPrograma;
    },
    [props.allProfilesForProgramas, props.roles]
  );

  const [subListReference, setSubListReference] = useState<References | string>(
    References.OPENED_ACTIVE
  );

  const isOpenedByReference = useCallback(
    (reference: References | string) => {
      return reference === subListReference;
    },
    [subListReference]
  );

  const setOpenedByReference = useCallback(
    (reference: References | string) => {
      if (isOpenedByReference(reference)) {
        setSubListReference(References.FORCE_CLOSE);
      } else {
        setSubListReference(reference);
      }
    },
    [isOpenedByReference]
  );

  const handleClickLink = useCallback(
    (
      url: string | undefined,
      obj: {
        novaAba?: boolean;
        permissoes?: MenuPermissions;
        internal: boolean;
        labelPrograma?: boolean;
      }
    ) => {
      if (!url) return;
      const target = obj.novaAba ? '_blank' : '_self';
      const isPermitted = !obj.permissoes || hasPermission(obj.permissoes);
      const watchIncongruencia = obj.permissoes && obj.permissoes.incongruencia;

      if (
        watchIncongruencia &&
        props.incongruencia &&
        typeof props.openModalIncongruencia === 'function'
      ) {
        props.openModalIncongruencia();
      } else if (isPermitted) {
        if (obj.internal) {
          props.history.push(url.replace(window.location.origin, ''));
        } else {
          window.open(url, target);
        }
        setOpenedByReference(References.OPENED_ACTIVE);
      } else if (obj.permissoes) {
        const isPrograma =
          obj.permissoes.programas && !!obj.permissoes.programas.length;
        const plural = isPrograma
          ? obj.permissoes.programas && obj.permissoes.programas.length > 1
          : obj.permissoes.roles && obj.permissoes.roles.length > 1;

        const label = isPrograma
          ? obj.permissoes.programas
              ?.map((prog) => props.getProgramaLabel(prog) ?? '')
              .join(', ')
          : obj.permissoes.roles?.join(', ');

        props.openModalProfileWarning(
          label || '',
          plural,
          !isPrograma || (obj.permissoes && obj.permissoes.forcePerfilLabel),
          obj.labelPrograma
        );
        setOpenedByReference(References.FORCE_CLOSE);
      }
      props.closeMenu();
    },
    [hasPermission, props, setOpenedByReference]
  );

  const getSubList = useCallback(
    (itens?: ItemBase[]) => {
      if (!itens || !itens.length) return undefined;

      return itens
        .sort(sortItems)
        .filter((sub) => {
          if (sub.permissoes && sub.permissoes.hide) {
            return hasPermission(sub.permissoes);
          }
          return true;
        })
        .map((sub) => {
          return {
            id: sub.id,
            icon: <ListDotIcon />,
            label: sub.label,
            active: sub.regex
              ? props.isRoute(window.location.pathname, new RegExp(sub.regex))
              : false,
            onClick: () => {
              if (sub.url)
                handleClickLink(sub.url, {
                  novaAba: sub.novaAba,
                  permissoes: sub.permissoes,
                  internal: Boolean(
                    sub.idFerramenta && sub.idFerramenta === props.idFerramenta
                  ),
                  labelPrograma: sub.labelPrograma,
                });
            },
          };
        });
    },
    [handleClickLink, hasPermission, props]
  );

  const menus = useMemo(() => {
    if (!props.menus) return [];
    return props.menus
      .sort(sortItems)
      .filter((item) => {
        if (
          (item.permissoes && item.permissoes.hide) ||
          item.subItems?.some((s) => s.permissoes && s.permissoes.hide)
        ) {
          return !item.subItems?.every(
            (s) =>
              s.permissoes && s.permissoes.hide && !hasPermission(s.permissoes)
          );
        }
        return true;
      })
      .map<MenuItemProps>((it) => {
        return {
          id: it.id,
          icon: stringToReactElement(it.icon, {
            style: { height: '24px', width: '24px' },
          }),
          label: it.label,
          active: it.regex
            ? props.isRoute(window.location.pathname, new RegExp(it.regex))
            : false,
          onClick: it.url
            ? () =>
                handleClickLink(it.url, {
                  novaAba: it.novaAba ?? false,
                  permissoes: it.permissoes,
                  internal: Boolean(
                    it.idFerramenta && it.idFerramenta === props.idFerramenta
                  ),
                  labelPrograma: it.labelPrograma,
                })
            : () => {
                setOpenedByReference(it.id);
              },
          subListOpened: isOpenedByReference(it.id),
          subList: getSubList(it.subItems),
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenedByReference, props.menus, props.history.location, props.roles]);

  return menus;
}
